import React, { useCallback } from 'react';

import { NumberInput } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';

import ItemUnavailableMessage from 'components/item-unavailable-message';
import { useProductPrice } from 'hooks/menu/use-product-price';
import { useMenuContext } from 'state/menu';
import { useOrderContext } from 'state/order';
import { useProductWizardContext } from 'state/product-wizard';
import { useEditingCartEntry } from 'state/product-wizard/hooks/use-editing-cart-entry';
import { MIN_CART_QUANTITY, getMenuObjectCartQuantity } from 'utils/cart';

import { StyledActionButton, StyledNumberInputWrapper } from '../styled';
import { useProductAvailability } from '../use-product-availability';
import { useProductCart } from '../use-product-cart';

import { ProductCtaWrapper } from './product-cta-wrapper';

export const ProductHomeCta: React.FC = () => {
  const { isOffer } = useMenuContext();
  const { cartEntries, serviceMode } = useOrderContext();
  const { productQuantity, selectedProduct, setProductQuantity } = useProductWizardContext();

  const { formatMessage } = useIntl();
  const { addToOrder } = useProductCart();
  const editingCartEntry = useEditingCartEntry();
  const cartIdEditing = editingCartEntry?.cartId;

  const { totalPrice, formattedProductPrice } = useProductPrice({
    shouldIncludeModifications: true,
    shouldIncludeQuantity: true,
  });
  const { dayParts, isItemAvailableForDaypart, itemDayParts } = useProductAvailability();

  const { availableCartQuantity, maxCartQuantityMet: isLimitReached } = getMenuObjectCartQuantity({
    menuObject: selectedProduct,
    quantityToAdd: productQuantity,
    cartEntries,
    cartIdEditing,
    serviceMode,
  });
  const maxCartQuantityMet = isLimitReached && !editingCartEntry;

  const handleQuantityUpdate = useCallback(
    value => {
      setProductQuantity(value);
    },
    [setProductQuantity]
  );

  const showIncrementor = !isOffer && !maxCartQuantityMet && isItemAvailableForDaypart;
  const disableIncrementQuantity = maxCartQuantityMet;
  const disableIncrementor = maxCartQuantityMet;

  // If price is not set correctly, don't allow adding such an item to cart, except offers
  const isCurrentPriceValid = totalPrice() > 0 || isOffer;
  const productName = selectedProduct?.name?.locale;

  const messageId = editingCartEntry
    ? 'updateQuantityAndItemWithPrice'
    : 'addQuantityAndItemWithPrice';

  const ariaLabel = formatMessage(
    { id: messageId },
    { itemName: productName, quantity: productQuantity, price: formattedProductPrice }
  );

  const addToOrderText = formatMessage({
    id: editingCartEntry ? 'update' : 'addToCart',
  });

  const label = maxCartQuantityMet
    ? formatMessage({ id: 'limitReached' })
    : `${addToOrderText} - ${formattedProductPrice}`;

  const handleClick = () => {
    addToOrder();
  };

  return (
    <ProductCtaWrapper
      button={
        isItemAvailableForDaypart ? (
          <StyledActionButton
            aria-label={ariaLabel}
            data-testid="product-home-action-button"
            disabled={!isCurrentPriceValid || maxCartQuantityMet}
            onClick={handleClick}
            fullWidth
            maintainWidth
          >
            {label}
          </StyledActionButton>
        ) : (
          <ItemUnavailableMessage
            dayParts={dayParts}
            itemDayParts={itemDayParts}
            marginAndBorders={false}
          />
        )
      }
      incrementor={
        showIncrementor && (
          <StyledNumberInputWrapper>
            <NumberInput
              aria-label={`Quantity ${productQuantity} of ${productName}`}
              value={productQuantity}
              onChange={handleQuantityUpdate}
              min={MIN_CART_QUANTITY}
              max={availableCartQuantity}
              disableIncrement={disableIncrementQuantity}
              disabled={disableIncrementor}
              decrement-aria-label={`decrement-${productName}`}
              increment-aria-label={`increment-${productName}`}
            />
          </StyledNumberInputWrapper>
        )
      }
    />
  );
};
